import { Component, inject, OnInit, computed } from '@angular/core';
import { AccountingExportTypes } from '@models/businessSettings/settings';
import { MatterFileType } from '@models/matter';
import {
	StatementOfAccountDefault,
	StatementOfAccountDefaultTypes,
	StatementOfAccountType,
} from '@models/statementOfAccount';
import { UntilDestroy } from '@ngneat/until-destroy';
import { RadioGroupItem } from 'src/app/shared/atomic/atoms/radio-group/radio-group.component';
import { ICON } from '@core/util/icon';
import { soaDefaultsTitles } from './soa-defaults.model';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import {
	BusinessDispatch,
	BusinessFeatureFlagSelectors,
	BusinessState,
} from '@state/business';
import { DirectionOfFeeType } from '@models/businessSettings/directionOfFees';
import {
	AccountingExportIdentifierTypes,
	AccountingExportSettings,
} from '@models/businessSettings/settings';
import { TitleSearchFeeSettings } from '@models/titleSearch';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { MatDialog } from '@angular/material/dialog';
import { SolunoImportModalComponent } from './soluno-import-modal/soluno-import-modal.component';
import { GhostPracticeImportModalComponent } from './ghost-practice-import-modal/ghost-practice-import-modal.component';
import { SolunoActivityCode } from '@models/soluno';
import { GhostPracticeCode } from '@models/ghost-practice';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { LeapApiService } from '../../../../shared/api-repos/leap/leap-api.service';
import { finalize, take } from 'rxjs/operators';
import { SnackbarService } from '@services/ui/snackbar.service';
import { Logger } from '@core/logger';
import { LeapManageModalComponent } from './leap-manage-modal/leap-manage-modal.component';
import {
	leapNullItemCostRecoveryTaskCode,
	leapNullItemFeeTaskCode,
} from '@models/leap';
import { BusinessStatamentOfAccountSelectors } from '@state/business/statementOfAccountDefaults/statementOfAccountDefaults.selectors';
import { select, Store } from '@ngxs/store';
import { AppState } from 'src/app/shared/state/app/app.state';

const fileTypeOptions: RadioGroupItem<MatterFileType>[] = [
	{
		value: MatterFileType.SALE,
		label: 'Sale',
	},
	{
		value: MatterFileType.PURCHASE,
		label: 'Purchase',
	},
	{
		value: MatterFileType.REFINANCE,
		label: 'Refinance',
	},
];

@UntilDestroy()
@Component({
	selector: 'app-soa-defaults',
	templateUrl: './defaults.component.html',
	standalone: false,
})
export class StatementOfAccountDefaultsComponent implements OnInit {
	mergedStatements = inject(Store).selectSignal<StatementOfAccountDefault[]>(
		BusinessStatamentOfAccountSelectors.getMergedDefaults,
	);

	statements = computed(() =>
		this.mergedStatements().filter(this.filterStatements),
	);

	ghostPracticeAnchorText = computed(() =>
		this.ghostPracticeFileName()
			? 'Review Accounting Codes'
			: 'Add Accounting Codes',
	);

	@SelectSnapshot(AppState.loadedFromLeap) loadedFromLeap: boolean;
	@SelectSnapshot(BusinessState.getTreefortConnected) treefortConnected;
	@SelectSnapshot(BusinessState.getDefaultIdvFeeSettings) defaultIdvFeeSettings;
	@SelectSnapshot(BusinessState.getAccountingExportSettings)
	accountingExportSettings: AccountingExportSettings;
	@SelectSnapshot(BusinessState.getDefaultTitleSearchFeeSettings)
	defaultTitleSearchFeeSettings: TitleSearchFeeSettings;
	@ViewSelectSnapshot(BusinessState.isSolunoEnabled) isSolunoEnabled: boolean;
	@ViewSelectSnapshot(BusinessState.isCosmolexEnabled)
	isCosmolexEnabled: boolean;
	@ViewSelectSnapshot(BusinessState.isLeapEnabled) isLeapEnabled: boolean;
	@ViewSelectSnapshot(BusinessState.isGhostPracticeEnabled)
	isGhostPracticeEnabled: boolean;
	@ViewSelectSnapshot(BusinessState.getSolunoActivityCodes)
	solunoActivityCodes: DropdownOption<SolunoActivityCode>[];
	@ViewSelectSnapshot(BusinessState.getGhostPracticeHSTDisbursementCodes)
	ghostPracticeHSTDisbursementCodes: GhostPracticeCode[];
	@ViewSelectSnapshot(BusinessState.getGhostPracticeNonHSTDisbursementCodes)
	ghostPracticeNonHSTDisbursementCodes: GhostPracticeCode[];

	ghostPracticeFileName = select(BusinessState.getGhostPracticeFileName);

	@ViewSelectSnapshot(BusinessFeatureFlagSelectors.isGhostPracticeEnabled)
	isGhostPracticeFeatureFlagEnabled: boolean;
	public ICON = ICON;
	public fileTypesOptions = fileTypeOptions;
	public selectedFileType = MatterFileType.SALE;
	public statementTypes = StatementOfAccountType;
	public importing = false;

	public feeItems = [];
	public hstItems = [];
	public nonHstItems = [];

	public soaDefaultsTitles = soaDefaultsTitles;
	public accountingExportOptionLabel = '';
	public accountingExportOptions: DropdownOption<AccountingExportTypes>[] = [
		{ name: 'None', value: null },
		{ name: 'Cosmolex', value: AccountingExportTypes.COSMOLEX },
		{ name: 'Quickbooks', value: AccountingExportTypes.QUICKBOOKS },
	];

	public accountingIdentifierOptions = [
		{
			label: 'File number',
			value: AccountingExportIdentifierTypes.FILE_NUMBER,
		},
		{
			label: 'Accounting number',
			value: AccountingExportIdentifierTypes.ACCOUNTING_NUMBER,
		},
	];

	public AccountingExportIdentifierTypes = AccountingExportIdentifierTypes;

	constructor(
		private businessDispatch: BusinessDispatch,
		private dialog: MatDialog,
		private analyticsService: AnalyticsService,
		private leapApiService: LeapApiService,
		private bar: SnackbarService,
	) {}

	ngOnInit(): void {
		this.accountingExportOptions.push({
			name: 'Soluno',
			value: AccountingExportTypes.SOLUNO,
		});

		if (this.isGhostPracticeFeatureFlagEnabled) {
			this.accountingExportOptions.push({
				name: 'GhostPractice',
				value: AccountingExportTypes.GHOSTPRACTICE,
			});
		}

		if (this.loadedFromLeap) {
			this.accountingExportOptions.length = 0; // clear it
			this.accountingExportOptions.push({
				name: 'LEAP',
				value: AccountingExportTypes.LEAP,
			});
		}

		this.accountingExportOptionLabel = this.accountingExportSettings
			.accountingPlatform
			? this.accountingExportOptions.find(
					(option) =>
						option.value === this.accountingExportSettings.accountingPlatform,
				)?.name
			: 'None';
	}

	private filterStatements = (
		statementOfAccount: StatementOfAccountDefault,
	) => {
		if (
			statementOfAccount.defaultType === StatementOfAccountDefaultTypes.IDV_FEE
		) {
			return (
				this.treefortConnected &&
				this.defaultIdvFeeSettings.location === DirectionOfFeeType.SOA &&
				statementOfAccount.type ===
					(this.defaultIdvFeeSettings.hst
						? StatementOfAccountType.HST
						: StatementOfAccountType.NON_HST)
			);
		}

		if (
			![
				StatementOfAccountDefaultTypes.HST_TITLE,
				StatementOfAccountDefaultTypes.NON_HST_TITLE,
				StatementOfAccountDefaultTypes.HST_WRIT,
				StatementOfAccountDefaultTypes.NON_HST_WRIT,
			].includes(
				statementOfAccount.defaultType as StatementOfAccountDefaultTypes,
			)
		) {
			return true; // Always keep items that aren't the four types
		}

		if (
			this.defaultTitleSearchFeeSettings.applyHstAll &&
			this.defaultTitleSearchFeeSettings.combineTitleAndWritsFees
		) {
			// Only keep HST_TITLE
			if (
				statementOfAccount.defaultType ===
				StatementOfAccountDefaultTypes.HST_TITLE
			) {
				statementOfAccount.description = 'Property and writ search costs';
				statementOfAccount.globalDescription = 'Property and writ search costs';
			}
			return (
				statementOfAccount.defaultType ===
				StatementOfAccountDefaultTypes.HST_TITLE
			);
		} else if (this.defaultTitleSearchFeeSettings.applyHstAll) {
			// Only keep HST_TITLE and HST_WRIT
			if (
				statementOfAccount.defaultType ===
				StatementOfAccountDefaultTypes.HST_TITLE
			) {
				statementOfAccount.description = 'Property search costs';
			}
			return (
				statementOfAccount.defaultType ===
					StatementOfAccountDefaultTypes.HST_TITLE ||
				statementOfAccount.defaultType ===
					StatementOfAccountDefaultTypes.HST_WRIT
			);
		} else if (this.defaultTitleSearchFeeSettings.combineTitleAndWritsFees) {
			if (
				statementOfAccount.defaultType ===
					StatementOfAccountDefaultTypes.HST_TITLE ||
				statementOfAccount.defaultType ===
					StatementOfAccountDefaultTypes.NON_HST_TITLE
			) {
				statementOfAccount.description = 'Property and writ search costs';
				statementOfAccount.globalDescription = 'Property and writ search costs';
			}
			// Only keep HST_TITLE and NON_HST_TITLE
			return (
				statementOfAccount.defaultType ===
					StatementOfAccountDefaultTypes.HST_TITLE ||
				statementOfAccount.defaultType ===
					StatementOfAccountDefaultTypes.NON_HST_TITLE
			);
		}
		return true;
	};

	public openSolunoImportModal() {
		this.analyticsService.logEvent(
			AnalyticsEvent.SOLUNO_BUSINESS_SETTINGS_MANAGE_CODES_CLICK,
		);
		this.dialog.open(SolunoImportModalComponent);
	}

	public openGhostPracticeImportModal() {
		this.analyticsService.logEvent(
			AnalyticsEvent.GHOST_PRACTICE_BUSINESS_SETTINGS_MANAGE_CODES_CLICK,
		);
		this.dialog.open(GhostPracticeImportModalComponent);
	}

	public openLeapManageModal() {
		this.analyticsService.logEvent(
			AnalyticsEvent.LEAP_BUSINESS_SETTINGS_MANAGE_CODES_CLICK,
		);
		this.dialog.open(LeapManageModalComponent); // TODO: Farid show the screenshots
	}

	public async importLeap() {
		this.importing = true;

		// Get the codes from our C# API
		this.leapApiService
			.getTaskCodes()
			.pipe(
				take(1),
				finalize(() => {
					this.importing = false;
				}),
			)
			.subscribe({
				next: (response) => {
					response.feeTaskCodes.unshift(leapNullItemFeeTaskCode);
					response.costRecoveryTaskCodes.unshift(
						leapNullItemCostRecoveryTaskCode,
					);
					this.businessDispatch.SetLeapTaskCodes(response);
				},
				error: (err) => {
					const message =
						'Unexpected error occurred during importing activity and recovery codes.';
					this.bar.error(message);

					Logger.error(
						message +
							' The unexpected error may be in our C# API or it may have occurred on the LEAP side. ' +
							JSON.stringify(err),
					);
				},
			});
	}

	public async onSelectAccountingExporting(
		accountingExportOption: DropdownResult<
			DropdownOption<AccountingExportTypes>
		>,
	) {
		if (
			accountingExportOption.result.value === AccountingExportTypes.COSMOLEX
		) {
			this.analyticsService.logEvent(
				AnalyticsEvent.COSMOLEX_BUSINESS_SETTING_SELECTION,
			);
		} else if (
			accountingExportOption.result.value === AccountingExportTypes.SOLUNO
		) {
			this.analyticsService.logEvent(AnalyticsEvent.SOLUNO_BUSINSSES_SELECTION);
		} else if (
			accountingExportOption.result.value === AccountingExportTypes.LEAP
		) {
			this.analyticsService.logEvent(AnalyticsEvent.LEAP_BUSINSSES_SELECTION);
		}

		this.businessDispatch.UpdateDefaultSettings({
			accountingExport: {
				accountingPlatform: accountingExportOption.result.value,
			} as AccountingExportSettings,
		});

		this.accountingExportOptionLabel = accountingExportOption.result.name;
	}

	public onSelectAccountingIdentifier(
		accountingIdentifierOption: AccountingExportIdentifierTypes,
	) {
		if (
			accountingIdentifierOption === AccountingExportIdentifierTypes.FILE_NUMBER
		) {
			this.analyticsService.logEvent(
				AnalyticsEvent.ACCOUNTING_ACCOUNTING_NUMBER_CHANGE_TO_FILE_NUMBER,
			);
		} else if (
			accountingIdentifierOption ===
			AccountingExportIdentifierTypes.ACCOUNTING_NUMBER
		) {
			this.analyticsService.logEvent(
				AnalyticsEvent.ACCOUNTING_ACCOUNTING_NUMBER_CHANGE_TO_ACCOUNTING_NUMBER,
			);
		}

		this.businessDispatch.UpdateDefaultSettings({
			accountingExport: {
				accountingIdentifier: accountingIdentifierOption,
			} as AccountingExportSettings,
		});
	}
}
